import { useState, useEffect } from 'react';
import { X, Zap, Loader2 } from 'lucide-react';
import type { AutomationField } from '@sca-uk/shared';
import { useZapierAutomations } from '@hooks/useAutomations';
import { triggerAutomation } from '@lib/automations';
import { usePermissions } from '@utils/permissions';

interface Props {
  personId: number;
  personName: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called with the new record ID after submission succeeds */
  onTriggered: (recordId: string) => void;
}

export default function TriggerAutomationModal({
  personId,
  personName,
  isOpen,
  onClose,
  onTriggered,
}: Props) {
  const { settings, isLoading: loadingAutomations } = useZapierAutomations();
  const { role } = usePermissions();
  const [selectedAutomationId, setSelectedAutomationId] = useState<string>('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter automations to those that are active and allowed for the current role
  const availableAutomations = (settings?.automations ?? []).filter(
    (a) => a.isActive && role && a.allowedRoles.includes(role as 'Admin' | 'Regular'),
  );

  // Get the currently selected automation
  const selectedAutomation =
    availableAutomations.find((a) => a.id === selectedAutomationId) ?? null;

  // Auto-select if there's only one automation
  useEffect(() => {
    if (availableAutomations.length === 1 && !selectedAutomationId) {
      setSelectedAutomationId(availableAutomations[0]!.id);
    }
  }, [availableAutomations, selectedAutomationId]);

  // Initialise field values when automation changes
  useEffect(() => {
    if (!selectedAutomation) {
      setFieldValues({});
      return;
    }

    const initial: Record<string, string> = {};
    for (const field of selectedAutomation.fields) {
      if (field.source === 'beacon_name') {
        initial[field.key] = personName;
      } else if (field.source === 'auto_date') {
        initial[field.key] = new Date().toISOString().split('T')[0]!;
      } else if (field.defaultValue) {
        initial[field.key] = field.defaultValue;
      } else {
        initial[field.key] = '';
      }
    }
    setFieldValues(initial);
  }, [selectedAutomation, personName]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleFieldChange = (key: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAutomation) return;

    // Validate required fields
    for (const field of selectedAutomation.fields) {
      if (field.required && !fieldValues[field.key]?.trim()) {
        setError(`${field.displayName} is required`);
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const recordId = await triggerAutomation({
        automationId: selectedAutomation.id,
        personId,
        personName,
        fieldValues,
      });
      onTriggered(recordId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger automation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-sca-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">Trigger Automation</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Person name (read-only info) */}
            <div className="px-3 py-2 bg-gray-50 rounded-md text-sm text-gray-600">
              Triggering automation for{' '}
              <span className="font-medium text-gray-900">{personName}</span>
            </div>

            {/* Automation selector */}
            {loadingAutomations ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading automations...
              </div>
            ) : availableAutomations.length === 0 ? (
              <div className="p-3 bg-amber-50 text-amber-700 text-sm rounded-md">
                No active automations available for your role. An admin needs to configure automations
                in System Settings.
              </div>
            ) : (
              <>
                {availableAutomations.length > 1 && (
                  <div>
                    <label className="label">Automation</label>
                    <select
                      className="input"
                      value={selectedAutomationId}
                      onChange={(e) => setSelectedAutomationId(e.target.value)}
                    >
                      <option value="">Select an automation...</option>
                      {availableAutomations.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                    {selectedAutomation?.description && (
                      <p className="mt-1 text-xs text-gray-500">{selectedAutomation.description}</p>
                    )}
                  </div>
                )}

                {availableAutomations.length === 1 && selectedAutomation?.description && (
                  <p className="text-xs text-gray-500 -mt-1">{selectedAutomation.description}</p>
                )}

                {/* Dynamic fields */}
                {selectedAutomation?.fields.map((field) => (
                  <FieldInput
                    key={field.key}
                    field={field}
                    value={fieldValues[field.key] ?? ''}
                    onChange={(v) => handleFieldChange(field.key, v)}
                  />
                ))}
              </>
            )}

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 text-red-700 text-sm rounded-md">{error}</div>
            )}
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="btn-primary"
              disabled={
                isSubmitting || !selectedAutomation || availableAutomations.length === 0
              }
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Triggering...
                </span>
              ) : (
                'Trigger'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Field Input Component ────────────────────────────────────────────────────

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: AutomationField;
  value: string;
  onChange: (value: string) => void;
}) {
  const isReadOnly = field.source === 'beacon_name' || field.source === 'auto_date';

  return (
    <div>
      <label className="label">
        {field.displayName}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      {field.fieldType === 'textarea' ? (
        <textarea
          className="input min-h-[80px]"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={isReadOnly}
          rows={3}
        />
      ) : field.fieldType === 'date' ? (
        <input
          type="date"
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={isReadOnly}
        />
      ) : field.fieldType === 'currency' ? (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
            &pound;
          </span>
          <input
            type="number"
            className="input pl-7"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            step="0.01"
            min="0"
            readOnly={isReadOnly}
          />
        </div>
      ) : field.fieldType === 'number' ? (
        <input
          type="number"
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={isReadOnly}
        />
      ) : (
        <input
          type="text"
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={isReadOnly}
        />
      )}

      {field.source === 'beacon_name' && (
        <p className="mt-1 text-xs text-gray-400">Auto-populated from Beacon</p>
      )}
      {field.source === 'auto_date' && (
        <p className="mt-1 text-xs text-gray-400">Automatically set to today</p>
      )}
    </div>
  );
}
